import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { verifyOtp as verifyOtpApi, requestOtp as requestOtpApi } from './api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [patientId, setPatientId] = useState(null);
    const [loading, setLoading] = useState(true);

    // ✅ FIX: Use a ref for the timeout ID. Refs don't cause re-renders when they are updated.
    const timeoutIdRef = useRef(null);

    // Logout function
    const logout = useCallback(() => {
        console.log("Logging out patient...");
        // Clear the timeout using the ref's current value
        if (timeoutIdRef.current) {
            clearTimeout(timeoutIdRef.current);
        }
        setIsAuthenticated(false);
        setPatientId(null);
        localStorage.removeItem('patientToken');
        localStorage.removeItem('patientId');
        window.location.href = '/patientlogin';
    }, []); // No dependencies needed now

    // Function to set up auto-logout timer
    // ✅ FIX: This function is now stable and will not be re-created on every call.
    const setupAutoLogout = useCallback(() => {
        // Clear any existing timer
        if (timeoutIdRef.current) {
            clearTimeout(timeoutIdRef.current);
        }

        const LOGOUT_TIME_MS = 30 * 60 * 1000;
        // const LOGOUT_TIME_MS = 1 * 60 * 1000; // For testing (1 minute)

        // Set a new timer and store its ID in the ref
        timeoutIdRef.current = setTimeout(() => {
            console.log("Session expired. Auto-logging out.");
            logout(); // Call the stable logout function
        }, LOGOUT_TIME_MS);

        console.log(`Auto-logout scheduled for ${LOGOUT_TIME_MS / 60 / 1000} minutes.`);
    }, [logout]); // Depends only on the stable logout function

    // Effect to check token on initial load (runs only once)
    useEffect(() => {
        const token = localStorage.getItem('patientToken');
        const storedPatientId = localStorage.getItem('patientId');

        if (token && storedPatientId) {
            setIsAuthenticated(true);
            setPatientId(storedPatientId);
            console.log("Found patient token and ID. Authenticated.");
        }
        setLoading(false);
    }, []);

    // Effect to manage the timer based on authentication status
    useEffect(() => {
        if (isAuthenticated) {
            setupAutoLogout();
        }

        // Cleanup function for when the component unmounts or user logs out
        return () => {
            if (timeoutIdRef.current) {
                clearTimeout(timeoutIdRef.current);
            }
        };
    }, [isAuthenticated, setupAutoLogout]);


    const login = async (mobile, otp) => {
        setLoading(true);
        try {
            const response = await verifyOtpApi(mobile, otp);
            if (response.data.success) {
                const { token, patientId: newPatientId } = response.data;
                localStorage.setItem('patientToken', token);
                localStorage.setItem('patientId', newPatientId);
                setIsAuthenticated(true);
                setPatientId(newPatientId);
                return { success: true, message: 'Login successful!' };
            } else {
                return { success: false, message: response.data.message };
            }
        } catch (error) {
            console.error("Login error:", error);
            return { success: false, message: error.response?.data?.message || 'Login failed.' };
        } finally {
            setLoading(false);
        }
    };

    const requestPatientOtp = async (mobile) => {
        try {
            const response = await requestOtpApi(mobile);
            return response.data;
        } catch (error) {
            console.error("Request OTP error:", error);
            throw error;
        }
    };

    const value = {
        isAuthenticated,
        patientId,
        loading,
        login,
        logout,
        requestPatientOtp,
        setupAutoLogout // This function is now stable
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};